import { Tag } from '@sm-dashboard/types';
import { routes } from '@/app/app';
import { toTagPermissionStoragePath } from '@/util/tagPermissionRoute';

export const ADMIN_BRANCHES_TAG = 'מנהל מערכת- אגף הסניפים';
export const ADMIN_SYSTEM_TAG = 'מנהל מערכת';
export const SUPER_ADMIN_TAG = 'מנהל מערכת - סופר אדמין';

export class UserPermission {
  private allowedPages: Set<string>;
  public userTags: Set<string>;
  public isAdmin = false;
  public isAdminBranches = false;
  public isSuperAdmin = false;
  public operations: number[] = [];
  public sharedCrmMembers: number[] = [];
  public sharedBidirectionalWorkMembers: number[] = [];
  public sharedWorkMembers: number[] = [];
  public sharedPaymentMembers: number[] = [];

  private formatPage(page: string): string {
    return toTagPermissionStoragePath(page);
  }

  constructor(
    tags: Tag[],
    userTags: string[],
    sharedCrmMembers: number[] = [],
    sharedBidirectionalWorkMembers: number[] = [],
    sharedWorkMembers: number[] = [],
    sharedPaymentMembers: number[] = []
  ) {
    this.allowedPages = new Set<string>();
    this.userTags = new Set(userTags);
    this.isAdmin = userTags.some(tag => tag === ADMIN_SYSTEM_TAG) || userTags.some(tag => tag === ADMIN_BRANCHES_TAG);
    this.isAdminBranches = userTags.some(tag => tag === ADMIN_BRANCHES_TAG);
    this.isSuperAdmin = userTags.some(tag => tag === SUPER_ADMIN_TAG);
    this.sharedCrmMembers = sharedCrmMembers;
    this.sharedBidirectionalWorkMembers = sharedBidirectionalWorkMembers;
    this.sharedWorkMembers = sharedWorkMembers;
    this.sharedPaymentMembers = sharedPaymentMembers;
    tags.forEach(tagObj => {
      if (this.userTags.has(tagObj.tag)) {
        try {
          const pages = JSON.parse(tagObj?.hasPermissionPages || '');

          if (pages.includes('גישה חופשית') || pages.includes('free-access')) {
            this.allowedPages = new Set(routes.map(route => this.formatPage(route.route)));
            this.allowedPages.add('free-access');
            return;
          }

          pages.forEach((page: string) => {
            this.allowedPages.add(this.formatPage(page));
          });
        } catch (e) {
          console.error(`Failed to parse hasPermissionPages for tag ${tagObj.tag}`);
        }
        if (tagObj.operations) {
          this.operations = [...this.operations, ...tagObj.operations];
        }
      }
    });
  }

  hasPagePermission(page: string): boolean {
    // Allow if page is empty or just slash
    if (!page || page === '/') {
      return true;
    }
    // Remove query parameters for permission check
    const [pathWithoutQuery] = page.split('?');
    // Remove trailing slashes except for root
    const normalizedPath = pathWithoutQuery === '/' ? pathWithoutQuery : pathWithoutQuery.replace(/\/+$/, '');
    // Remove all ID-like segments (number or hex string) from anywhere in the path
    const basePath = normalizedPath
      .split('/')
      .filter(segment => !/^([a-fA-F0-9]{8,}|[0-9]+)$/.test(segment))
      .join('/');
    // Ensure leading slash
    const formattedBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`;
    if (this.allowedPages.has(this.formatPage(formattedBasePath)) || this.allowedPages.has('free-access')) {
      return true;
    }
    return false;
  }

  hasTag(tagName: string): boolean {
    return this.userTags.has(tagName);
  }

  getAllowedPages(): string[] {
    return Array.from(this.allowedPages);
  }

  hasOnlyOneTag(tagName: string): boolean {
    return this.userTags.size === 1 && this.userTags.has(tagName);
  }

  isAdminTag(tagName: string): boolean {
    return tagName === ADMIN_SYSTEM_TAG || tagName === ADMIN_BRANCHES_TAG || tagName === SUPER_ADMIN_TAG;
  }

  hasSharedCrm(): boolean {
    return this.sharedCrmMembers.length > 0;
  }

  // Combined work scope: bidirectional peers + one-directional work members
  get workScopeMembers(): number[] {
    return [...new Set([...this.sharedBidirectionalWorkMembers, ...this.sharedWorkMembers])];
  }

  hasSharedWork(): boolean {
    return this.workScopeMembers.length > 0;
  }

  hasSharedPayment(): boolean {
    return this.sharedPaymentMembers.length > 0;
  }
}
export const hasCrmAccess = (userTags: string[]): boolean => {
  if (!userTags || userTags.length === 0) {
    return false;
  }
  const isAdmin = userTags.some(tag => tag === ADMIN_SYSTEM_TAG || tag === ADMIN_BRANCHES_TAG);
  if (isAdmin) {
    return true;
  }
  const hasCrmTag = userTags.includes('CRM');
  return hasCrmTag;
};
